<template>
  <v-dialog
    v-model="opened"
    content-class="income-edit"
    max-width="400"
    persistent
  >
    <h3 class="income-edit__title">
      {{ title }}
    </h3>

    <v-btn
      class="add-btn"
      color="success"
      icon
      :disabled="formData.length === paymentTypes.length"
      @click="addTransaction"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <v-btn class="close-btn" icon @click="opened = false">
      <v-icon size="27" color="#464646"> mdi-close-circle-outline </v-icon>
    </v-btn>

    <v-progress-linear
      v-if="itemLoading"
      style="border-radius:3px"
      color="warning"
      height="6"
      indeterminate
    />

    <transition name="fadeHeight">
      <v-form
        v-if="!itemLoading"
        ref="incomeEdit"
        @submit.prevent="onSubmit"
        :disabled="loading"
      >
        <fieldset
          v-for="(form, idx) in formData"
          :key="idx"
          class="income-edit__fieldset"
        >
          <div class="income-edit__fieldset-delete">
            <v-btn
              v-if="idx > 0"
              icon
              color="red"
              small
              @click="deleteTransaction(idx)"
            >
              <v-icon size="20" color="red"> mdi-delete-outline </v-icon>
            </v-btn>
          </div>
          <div class="income-edit__row">
            <v-text-field
              v-model="formData[idx].amount"
              outlined
              dense
              hide-details
              class="income-edit__row-field"
              :label="$t('common.fields.sum')"
              :rules="[isRequired, onlyNumbers]"
            />

            <v-select
              v-model="formData[idx].payment_type"
              outlined
              dense
              item-text="name"
              item-value="id"
              hide-details
              class="income-edit__row-field"
              :items="getPaymentTypes(idx)"
              :label="$t('common.fields.paymentMethod')"
              :rules="[isRequired]"
            />
          </div>

          <v-text-field
            v-model="formData[idx].comment"
            class="mb-4"
            outlined
            dense
            hide-details
            :label="$t('common.fields.comment')"
          />
        </fieldset>

        <v-btn
          color="warning"
          elevation="0"
          type="submit"
          block
          :loading="loading"
        >
          {{ $t('common.btns.save') }}
        </v-btn>
      </v-form>
    </transition>
  </v-dialog>
</template>

<script>
export default {
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    type: {
      type: Number,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    itemLoading: false,
    loading: false,
    transactions: [],
    formData: [],
  }),
  watch: {
    opened(val) {
      if (!val) {
        setTimeout(() => {
          this.loading = false;
          this.itemLoading = true;
          this.formData = [];
        }, 300);
      } else {
        this.fetchItemData();
      }
    },
  },
  computed: {
    opened: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('change', val);
      },
    },
    paymentTypes() {
      return this.$store.state['workers-board'].paymentTypes;
    },
  },
  methods: {
    isRequired(v) {
      return !!v || this.$t('common.fields.validation.field.required');
    },
    onlyNumbers(v) {
      if (!v) return true;
      return (
        /^[0-9]{1,12}$/.test(v) || this.$t('common.fields.validation.numbers')
      );
    },
    async fetchItemData() {
      this.itemLoading = true;
      const { transactions } = await this.$store.dispatch(
        'workers-board/fetchListItem',
        this.item.id,
      );

      this.transactions = transactions;
      this.itemLoading = false;
      this.fillForm();
    },
    fillForm() {
      const transactions = this.transactions.reduce((arr, item) => {
        if (item.type === this.type) {
          arr.push(item);
        }

        return arr;
      }, []);

      if (transactions.length === 0) {
        this.formData.push({
          amount: '',
          payment_type: null,
          comment: '',
        });
      }

      transactions.forEach((data) => {
        this.formData.push({
          amount: Number(data.amount).toString(),
          payment_type: String(data.payment_type),
          comment: data.comment,
          id: data.id,
        });
      });
    },
    addTransaction() {
      this.formData.push({
        amount: '',
        payment_type: null,
        comment: '',
      });
    },
    deleteTransaction(idx) {
      this.formData = this.formData.filter((_, index) => index !== idx);
    },
    getPaymentTypes(formIdx) {
      return this.paymentTypes.filter(({ id }) => {
        const isChecked = this.formData.some((item, idx) => {
          const checked = item.payment_type === id;
          const currentForm = idx === formIdx;

          return checked && !currentForm;
        });

        return !isChecked;
      });
    },
    async onSubmit() {
      const isValid = this.$refs.incomeEdit.validate();

      if (!isValid) return;

      this.loading = true;
      const transactions = this.transactions.filter(
        ({ type }) => type !== this.type,
      );
      const formData = this.formData.map((form) => ({
        ...form,
        type: this.type,
      }));

      const params = {
        car_id: this.item.car_id,
        driver_id: this.item.driver_id,
        transactions: [...transactions, ...formData],
      };

      const res = await this.$store.dispatch('workers-board/updateItem', {
        id: this.item.id,
        formData: params,
      });

      this.$emit('updated', { res, id: this.item.id });
    },
  },
};
</script>

<style lang="scss">
.income-edit {
  padding: 10px 15px;
  background-color: #fff;

  &__fieldset {
    position: relative;
    padding: 0;
    border: none;

    &:not(:first-child) {
      padding-top: 25px;
      margin-top: 10px;
      border-top: 1px dashed #777;
    }

    &-delete {
      display: flex;
      justify-content: flex-end;
      position: absolute;
      right: 0;
      top: -15px;
      width: 50px;
      background-color: #fff;
    }
  }

  &__title {
    color: #484848;
    font-size: 18px;
    margin-bottom: 10px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;

    &-field {
      max-width: 48%;
      width: 100% !important;
    }
  }
}

.fadeHeight-enter-active,
.fadeHeight-leave-active {
  transition: all 0.2s;
  max-height: 100%;
}
.fadeHeight-enter,
.fadeHeight-leave-to {
  opacity: 0;
  max-height: 0px;
}
</style>
